import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { PageHeader, Card, Modal, Field, Btn, Pill, Empty, inputCls } from "@/dashboard/ui";
import { PhoneCall, Pencil, Trash2, Plus, PhoneOff, AudioLines, X, ChevronDown, ChevronUp, MessageSquare, RefreshCw, Play, Square } from "lucide-react";

const VOICES_FALLBACK = ["Aria — Warm", "Kern — Calm", "Noa — Bright", "Alfie — Formal"];
const PREVIEW_LINES = {
  "English (UK)": "Hello, thanks for calling. How can I help you today?",
  "English (US)": "Hi there, thanks for calling. How can I help you today?",
  "Hindi (हिन्दी)": "नमस्ते! कॉल करने के लिए धन्यवाद। मैं आपकी क्या मदद कर सकती हूँ?",
  "Telugu (తెలుగు)": "నమస్తే! కాల్ చేసినందుకు ధన్యవాదాలు. నేను మీకు ఎలా సహాయపడగలను?",
  "Spanish": "¡Hola! Gracias por llamar. ¿En qué puedo ayudarle?",
  "French": "Bonjour ! Merci de votre appel. Comment puis-je vous aider ?",
};
const LANGUAGES = ["English (UK)", "English (US)", "Hindi (हिन्दी)", "Telugu (తెలుగు)", "Spanish", "French"];
const OBJECTIVES = ["Customer support", "Lead qualification", "Appointment enquiries", "General enquiries"];
const STYLES = ["Warm and friendly", "Professional", "Energetic", "Calm and reassuring"];
const EMPTY_FORM = {
  agent_name: "", business_name: "", instructions: "", greeting: "",
  voice: VOICES_FALLBACK[0], language: LANGUAGES[0], knowledge: "",
  objective: OBJECTIVES[0], booking_preferences: "", outcome_categories: "", faqs: [],
  speaking_style: STYLES[0], speaking_speed: 1.0, booking_enabled: false,
};

export default function VoiceAgents() {
  const [agents, setAgents] = useState(null);
  const [calls, setCalls] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [openSession, setOpenSession] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/voice-agents").then((r) => setAgents(r.data));
  useEffect(() => {
    load();
    api.get("/calls").then((r) => setCalls(r.data));
    api.get("/voice/sessions")
      .then((r) => setSessions(r.data.filter((s) => (s.messages || []).length > 0)))
      .catch(() => {});
    api.get("/voice-agents/voices")
      .then((r) => { if ((r.data.voices || []).length) setVoices(r.data.voices.map((v) => v.label)); })
      .catch(() => {});  // fallback catalogue keeps the form usable offline
    loadVoiceRequests();
  }, []);

  // Custom voice requests (queued pending a consented cloning provider)
  const [voices, setVoices] = useState(VOICES_FALLBACK);
  const [voiceRequests, setVoiceRequests] = useState([]);
  const [cvOpen, setCvOpen] = useState(false);
  const [cvName, setCvName] = useState("");
  const [cvFile, setCvFile] = useState(null);
  const [cvConfirmed, setCvConfirmed] = useState(false);
  const [cvBusy, setCvBusy] = useState(false);

  const loadVoiceRequests = () => api.get("/voice-agents/custom-voice-requests")
    .then((r) => setVoiceRequests(r.data.requests || [])).catch(() => {});

  const submitVoiceRequest = async () => {
    if (!cvFile || !cvName.trim() || !cvConfirmed) return;
    setCvBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", cvFile);
      fd.append("speaker_name", cvName.trim());
      fd.append("confirm_ownership", "true");
      await api.post("/voice-agents/custom-voice-requests", fd);
      toast.success("Request queued — we'll be in touch when a voice provider is connected");
      setCvOpen(false); setCvName(""); setCvFile(null); setCvConfirmed(false);
      loadVoiceRequests();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCvBusy(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Voice preview: plays the CURRENT (possibly unsaved) editor settings via the
  // metered /voice/speak endpoint. Server audio or a clear error — never a browser voice.
  const [previewState, setPreviewState] = useState("idle"); // idle | loading | playing
  const [previewError, setPreviewError] = useState("");
  const previewAudioRef = useRef(null);

  useEffect(() => () => { try { previewAudioRef.current?.pause(); } catch { /* noop */ } }, []);

  const previewVoice = async () => {
    if (previewState === "loading") return;                    // no overlapping requests
    if (previewState === "playing") {                          // explicit stop
      try { previewAudioRef.current?.pause(); } catch { /* noop */ }
      setPreviewState("idle");
      return;
    }
    setPreviewError("");
    setPreviewState("loading");
    try {
      // Cached server-side preview: repeat previews of the same voice are instant.
      const { data: catalog } = await api.get("/voice-agents/voices");
      const entry = (catalog.voices || []).find((v) => v.label === form.voice);
      if (!entry) throw new Error("unknown voice");
      const res = await api.get(`/voice-agents/voice-preview/${entry.id}`, {
        params: { text: PREVIEW_LINES[form.language] || PREVIEW_LINES["English (UK)"] },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      previewAudioRef.current = audio;
      audio.onended = () => { setPreviewState("idle"); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPreviewState("idle"); setPreviewError("Playback failed in this browser."); URL.revokeObjectURL(url); };
      await audio.play();
      setPreviewState("playing");
    } catch (e) {
      setPreviewState("idle");
      setPreviewError(e?.response?.status === 503
        ? "Speech engine not connected — preview unavailable."
        : "Preview failed — your settings were not changed.");
    }
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setError("");
    setModal({ mode: "add" });
  };
  const openEdit = (a) => {
    setForm({
      ...EMPTY_FORM, ...a,
      objective: a.objective || OBJECTIVES[0],
      outcome_categories: (a.outcome_categories || []).join(", "),
      faqs: a.faqs || [],
    });
    setError("");
    setModal({ mode: "edit", agent: a });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = {
        ...form,
        speaking_speed: Number(form.speaking_speed) || 1.0,
        outcome_categories: form.outcome_categories.split(",").map((s) => s.trim()).filter(Boolean),
        faqs: form.faqs.filter((f) => f.q.trim() || f.a.trim()),
      };
      if (modal.mode === "add") await api.post("/voice-agents", payload);
      else await api.put(`/voice-agents/${modal.agent.id}`, payload);
      setModal(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete agent "${a.agent_name}"?`)) return;
    await api.delete(`/voice-agents/${a.id}`);
    load();
  };

  const setFaq = (i, key, value) =>
    setForm((f) => ({ ...f, faqs: f.faqs.map((item, idx) => (idx === i ? { ...item, [key]: value } : item)) }));

  return (
    <div data-testid="voice-agents-page">
      <PageHeader
        title="AI Voice Agents"
        sub="Configure your calling agents — multilingual: English (UK & US), Hindi and Telugu. Saved to your account; live telephone answering switches on when the Twilio connection is enabled."
      >
        <Btn variant="ghost" data-testid="custom-voice-button" onClick={() => setCvOpen(true)}>
          Custom voice
        </Btn>
        <Btn data-testid="add-agent-button" onClick={openAdd}>
          <Plus size={15} /> New Agent
        </Btn>
      </PageHeader>

      {agents === null ? (
        <p className="py-10 text-center text-sm text-neutral-400">Loading…</p>
      ) : agents.length === 0 ? (
        <Empty
          testid="agents-empty"
          icon={PhoneCall}
          title="No agents configured"
          body="Create your first agent — name, greeting, instructions, FAQs, language and speaking style — ready for when calling goes live."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {agents.map((a) => (
            <Card key={a.id} className="p-5" data-testid={`agent-card-${a.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3.5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#111111] text-white">
                    <PhoneCall size={18} />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-[#111111]">{a.agent_name}</p>
                    <p className="text-xs text-neutral-500">{a.business_name || "No business name"}</p>
                  </div>
                </div>
                <Pill tone="outline">Not connected</Pill>
              </div>
              <dl className="mt-4 space-y-2 text-xs">
                {a.objective && <div className="flex gap-2"><dt className="w-20 shrink-0 font-semibold text-neutral-500">Objective</dt><dd className="text-neutral-700">{a.objective}</dd></div>}
                <div className="flex gap-2"><dt className="w-20 shrink-0 font-semibold text-neutral-500">Language</dt><dd className="text-neutral-700">{a.language}</dd></div>
                <div className="flex gap-2"><dt className="w-20 shrink-0 font-semibold text-neutral-500">Voice</dt><dd className="text-neutral-700">{a.voice} (placeholder)</dd></div>
                <div className="flex gap-2"><dt className="w-20 shrink-0 font-semibold text-neutral-500">Style</dt><dd className="text-neutral-700">{a.speaking_style || "Warm and friendly"} · {Number(a.speaking_speed || 1).toFixed(2)}× speed</dd></div>
                {a.greeting && <div className="flex gap-2"><dt className="w-20 shrink-0 font-semibold text-neutral-500">Greeting</dt><dd className="line-clamp-2 text-neutral-700">“{a.greeting}”</dd></div>}
                {(a.faqs || []).length > 0 && <div className="flex gap-2"><dt className="w-20 shrink-0 font-semibold text-neutral-500">FAQs</dt><dd className="text-neutral-700">{a.faqs.length} question{a.faqs.length === 1 ? "" : "s"} &amp; answers</dd></div>}
                {(a.outcome_categories || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {a.outcome_categories.map((o) => <Pill key={o} tone="grey">{o}</Pill>)}
                  </div>
                )}
              </dl>
              <div className="mt-4 flex items-center justify-between border-t border-neutral-100 pt-3">
                <Link
                  to={`/dashboard/talk?agent=${a.id}`}
                  data-testid={`agent-talk-${a.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#111111] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-neutral-800"
                >
                  <AudioLines size={13} /> Talk to this agent
                </Link>
                <div className="flex gap-1.5">
                  <button data-testid={`agent-sync-${a.id}`} aria-label="Sync from business profile" title="Fill empty fields from your Business Profile (never overwrites your edits)"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/voice-agents/${a.id}/sync-profile`);
                        toast.success(data.filled.length ? `Filled from profile: ${data.filled.join(", ")}` : "Nothing to fill — this agent is fully customised");
                        load();
                      } catch (e) { toast.error(formatApiError(e)); }
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100">
                    <RefreshCw size={14} />
                  </button>
                  <button data-testid={`agent-edit-${a.id}`} aria-label="Edit agent" onClick={() => openEdit(a)} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100">
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`agent-delete-${a.id}`} aria-label="Delete agent" onClick={() => remove(a)} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <h2 className="mb-3 mt-10 text-lg font-bold text-[#111111]">Talk conversations</h2>
      {sessions.length === 0 ? (
        <Empty
          testid="transcripts-empty"
          icon={MessageSquare}
          title="No conversations yet"
          body="Talk to an agent on the Talk to AI page and the saved transcript appears here for review."
        />
      ) : (
        <div className="space-y-3" data-testid="transcripts-section">
          {sessions.map((s) => {
            const agent = (agents || []).find((a) => a.id === s.agent_id);
            const speaker = agent?.agent_name || "Agent";
            return (
              <Card key={s.id} className="p-4" data-testid={`transcript-session-${s.id}`}>
                <button
                  className="flex w-full items-center justify-between text-left"
                  data-testid={`transcript-toggle-${s.id}`}
                  onClick={() => setOpenSession(openSession === s.id ? null : s.id)}
                >
                  <div>
                    <p className="text-sm font-semibold text-[#111111]">{agent?.agent_name || "Savy Kerno assistant"}</p>
                    <p className="text-xs text-neutral-400">
                      {String(s.created_at).replace("T", " ").slice(0, 16)} · {s.messages.length} messages
                    </p>
                  </div>
                  {openSession === s.id ? (
                    <ChevronUp size={16} className="shrink-0 text-neutral-400" />
                  ) : (
                    <ChevronDown size={16} className="shrink-0 text-neutral-400" />
                  )}
                </button>
                {openSession === s.id && (
                  <div className="mt-3 space-y-2 border-t border-neutral-100 pt-3" data-testid={`transcript-body-${s.id}`}>
                    {s.messages.map((m, i) => (
                      <p key={i} className="text-xs leading-relaxed">
                        <span className="font-bold text-neutral-500">{m.role === "user" ? "You" : speaker}: </span>
                        <span className="text-neutral-700">{m.text}</span>
                      </p>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <h2 className="mb-3 mt-10 text-lg font-bold text-[#111111]">Call history</h2>
      {calls && calls.calls.length === 0 ? (
        <Empty
          testid="call-history-empty"
          icon={PhoneOff}
          title="No calls yet — telephony not connected"
          body="Real call records (lead, number, direction, status, duration, outcome, date) will appear here once the LiveKit Agents / Pipecat engine is connected. No call history is fabricated."
        />
      ) : null}

      {modal && (
        <Modal testid="agent-modal" title={modal.mode === "add" ? "New voice agent" : `Edit ${modal.agent.agent_name}`} onClose={() => setModal(null)}>
          <form onSubmit={save} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Agent name *">
                <input data-testid="agent-name-input" className={inputCls} value={form.agent_name} onChange={set("agent_name")} required />
              </Field>
              <Field label="Business name">
                <input data-testid="agent-business-input" className={inputCls} value={form.business_name} onChange={set("business_name")} />
              </Field>
              <Field label="Call objective">
                <select data-testid="agent-objective-select" className={inputCls} value={form.objective} onChange={set("objective")}>
                  {[...new Set([...OBJECTIVES, form.objective].filter(Boolean))].map((o) => <option key={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Language" hint="English variant sets the accent; the agent still detects and switches languages">
                <select data-testid="agent-language-select" className={inputCls} value={form.language} onChange={set("language")}>
                  {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Voice">
                <select data-testid="agent-voice-select" className={inputCls} value={form.voice} onChange={set("voice")}>
                  {voices.map((v) => <option key={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Voice preview" hint="Hear the current voice, language, style and speed — uses your unsaved settings, nothing is saved">
                <div className="flex items-center gap-2">
                  <Btn variant="ghost" className="px-3 py-1.5 text-xs" data-testid="agent-preview-voice"
                    disabled={previewState === "loading"} onClick={previewVoice}>
                    {previewState === "loading" ? "Generating…"
                      : previewState === "playing" ? <><Square size={12} className="mr-1" /> Stop</>
                      : <><Play size={12} className="mr-1" /> Preview voice</>}
                  </Btn>
                  {previewError && <span className="text-[11px] text-red-600" data-testid="agent-preview-error">{previewError}</span>}
                </div>
              </Field>
              <Field label="Speaking style">
                <select data-testid="agent-style-select" className={inputCls} value={form.speaking_style} onChange={set("speaking_style")}>
                  {STYLES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={`Speaking speed — ${Number(form.speaking_speed).toFixed(2)}×`} hint="0.70 slow and calm · 1.00 natural · 1.30 brisk">
                <input data-testid="agent-speed-input" type="range" min="0.7" max="1.3" step="0.05" className="w-full accent-[#111111]" value={form.speaking_speed} onChange={set("speaking_speed")} />
              </Field>
              <Field label="Appointment booking preferences">
                <input data-testid="agent-booking-input" className={inputCls} value={form.booking_preferences} onChange={set("booking_preferences")} placeholder="e.g. Weekdays 9am–5pm, 30-minute slots" />
              </Field>
            </div>
            <label className="flex items-start gap-2.5 rounded-xl bg-neutral-50 px-4 py-3 text-sm font-medium text-neutral-700">
              <input
                type="checkbox"
                data-testid="agent-booking-enabled-checkbox"
                checked={!!form.booking_enabled}
                onChange={(e) => setForm((f) => ({ ...f, booking_enabled: e.target.checked }))}
                className="mt-0.5 h-4 w-4 accent-[#111111]"
              />
              <span>
                Enable real appointment booking in preview
                <span className="block text-xs font-normal text-neutral-500">
                  The agent checks your live availability and creates real bookings in your Appointments calendar, linked to your CRM. Off by default.
                </span>
              </span>
            </label>
            <Field label="Greeting message" hint="Translated naturally if the customer speaks another language">
              <textarea data-testid="agent-greeting-input" rows={2} className={inputCls} value={form.greeting} onChange={set("greeting")} placeholder="Hi, this is Savy Kerno calling on behalf of…" />
            </Field>
            <Field label="Agent instructions">
              <textarea data-testid="agent-instructions-input" rows={3} className={inputCls} value={form.instructions} onChange={set("instructions")} placeholder="How the agent should behave, qualify and respond…" />
            </Field>
            <Field label="Business FAQs" hint="Questions callers may ask, with the answers your agent should give">
              <div className="space-y-2.5">
                {form.faqs.map((faq, i) => (
                  <div key={i} className="relative space-y-1.5 rounded-xl border border-neutral-200 p-3">
                    <button
                      type="button"
                      aria-label="Remove FAQ"
                      data-testid={`faq-remove-${i}`}
                      onClick={() => setForm((f) => ({ ...f, faqs: f.faqs.filter((_, idx) => idx !== i) }))}
                      className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100"
                    >
                      <X size={13} />
                    </button>
                    <input data-testid={`faq-question-${i}`} className={inputCls} placeholder="Question, e.g. What are your opening hours?" value={faq.q} onChange={(e) => setFaq(i, "q", e.target.value)} />
                    <textarea data-testid={`faq-answer-${i}`} rows={2} className={inputCls} placeholder="Answer" value={faq.a} onChange={(e) => setFaq(i, "a", e.target.value)} />
                  </div>
                ))}
                <Btn type="button" variant="ghost" data-testid="faq-add-button" onClick={() => setForm((f) => ({ ...f, faqs: [...f.faqs, { q: "", a: "" }] }))}>
                  <Plus size={14} /> Add FAQ
                </Btn>
              </div>
            </Field>
            <Field label="Business knowledge">
              <textarea data-testid="agent-knowledge-input" rows={2} className={inputCls} value={form.knowledge} onChange={set("knowledge")} placeholder="Services, prices, service area…" />
            </Field>
            <Field label="Call outcome categories" hint="Comma-separated, e.g. Booked, Callback requested, Not interested">
              <input data-testid="agent-outcomes-input" className={inputCls} value={form.outcome_categories} onChange={set("outcome_categories")} />
            </Field>
            {error && <p data-testid="agent-form-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2.5">
              <Btn type="button" variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn data-testid="agent-save-button" type="submit" disabled={busy}>{busy ? "Saving…" : "Save Agent"}</Btn>
            </div>
          </form>
        </Modal>
      )}

      {cvOpen && (
        <Modal title="Request a custom voice" onClose={() => setCvOpen(false)} testid="custom-voice-modal">
          <div className="space-y-4 text-sm text-neutral-600">
            <p className="rounded-xl bg-neutral-50 p-3 text-xs leading-relaxed">
              Custom voice cloning needs a consent-verified voice provider, which is not connected yet.
              Your request is queued and the recording is <strong>not stored</strong> — nothing is cloned
              or synthesised until a provider is approved. We'll ask you to re-upload then.
            </p>
            <Field label="Speaker's name">
              <input className={inputCls} data-testid="cv-speaker-name" value={cvName}
                onChange={(e) => setCvName(e.target.value)} placeholder="Whose voice is this?" />
            </Field>
            <Field label="Short authorised recording (under 5MB)">
              <input type="file" accept="audio/*" data-testid="cv-file" className="text-xs"
                onChange={(e) => setCvFile(e.target.files?.[0] || null)} />
            </Field>
            <label className="flex items-start gap-2.5 rounded-xl border border-neutral-200 p-3 text-xs font-medium text-neutral-700">
              <input type="checkbox" data-testid="cv-confirm" checked={cvConfirmed}
                onChange={(e) => setCvConfirmed(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#111111]" />
              <span>I own this voice or hold the speaker's written permission to use it. I understand
                unauthorised voice cloning is prohibited and this request is recorded.</span>
            </label>
            {voiceRequests.length > 0 && (
              <div className="text-xs">
                <p className="mb-1 font-semibold text-neutral-500">Your requests</p>
                {voiceRequests.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 py-1">
                    <span>{r.speaker_name}</span>
                    <Pill tone="amber">{r.status.replace(/_/g, " ")}</Pill>
                  </div>
                ))}
              </div>
            )}
            <Btn data-testid="cv-submit" disabled={cvBusy || !cvFile || !cvName.trim() || !cvConfirmed}
              onClick={submitVoiceRequest}>
              {cvBusy ? "Submitting…" : "Queue request"}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
